import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for Supabase real-time subscriptions
 * @param {string} table - Table name to subscribe to
 * @param {function} onInsert - Callback for INSERT events
 * @param {function} onUpdate - Callback for UPDATE events
 * @param {function} onDelete - Callback for DELETE events
 * @param {object} filter - Optional filter for subscription
 */
export const useRealtimeSubscription = (
  table,
  { onInsert, onUpdate, onDelete, filter = {} }
) => {
  useEffect(() => {
    // Create channel name based on table
    const channelName = `${table}_changes`;
    
    // Subscribe to changes
    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          ...filter
        },
        (payload) => {
          console.log(`[Realtime] ${table} change:`, payload);
          
          // Handle different event types
          switch (payload?.eventType) {
            case 'INSERT':
              if (onInsert) onInsert(payload?.new);
              break;
            case 'UPDATE':
              if (onUpdate) onUpdate(payload?.new, payload?.old);
              break;
            case 'DELETE':
              if (onDelete) onDelete(payload?.old);
              break;
            default:
              console.log('Unknown event type:', payload?.eventType);
          }
        }
      )?.subscribe((status) => {
        console.log(`[Realtime] ${table} subscription status:`, status);
      });

    // Cleanup function
    return () => {
      console.log(`[Realtime] Unsubscribing from ${table}`);
      supabase?.removeChannel(channel);
    };
  }, [table, onInsert, onUpdate, onDelete, filter]);
};

/**
 * Hook for subscribing to multiple tables at once
 * @param {Array} subscriptions - Array of subscription configs
 */
export const useMultipleRealtimeSubscriptions = (subscriptions) => {
  useEffect(() => {
    const channels = subscriptions?.map((sub) => {
      const channelName = `${sub?.table}_changes`;
      
      return supabase?.channel(channelName)?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: sub?.table,
            ...(sub?.filter || {})
          },
          (payload) => {
            console.log(`[Realtime] ${sub?.table} change:`, payload);
            
            switch (payload?.eventType) {
              case 'INSERT':
                if (sub?.onInsert) sub?.onInsert(payload?.new);
                break;
              case 'UPDATE':
                if (sub?.onUpdate) sub?.onUpdate(payload?.new, payload?.old);
                break;
              case 'DELETE':
                if (sub?.onDelete) sub?.onDelete(payload?.old);
                break;
              default:
                console.log('Unknown event type:', payload?.eventType);
            }
          }
        )?.subscribe((status) => {
          console.log(`[Realtime] ${sub?.table} subscription status:`, status);
        });
    });

    // Cleanup all channels
    return () => {
      console.log('[Realtime] Unsubscribing from all channels');
      channels?.forEach(channel => supabase?.removeChannel(channel));
    };
  }, [subscriptions]);
};

export default useRealtimeSubscription;